import { z } from "zod";
import { patchOf } from "../common/patch.js";
import { localDateSchema, quantitySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
export const ASSET_STATUSES = ["ACTIVE", "OUT_OF_SERVICE", "DECOMMISSIONED"];
export const assetStatusSchema = z.enum(ASSET_STATUSES);
const positiveQuantity = quantitySchema.refine((value) => Number(value) > 0, "Sıfırdan büyük olmalıdır");
const nonNegativeQuantity = quantitySchema.refine((value) => Number(value) >= 0, "Negatif olamaz");
// Varsayılan EN DIŞTA durur: patchOf() yalnızca dıştaki varsayılanı sökebilir.
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .default(null);
const assetNo = z.number().int().min(1).max(99);
/**
 * Prompt 23 (ADR 0023 §2): bakım ve uyum için varlık yaşam döngüsü alanları — seri no, kurulum, garanti bitişi, servis
 * firması (tedarikçi carisi). Girdi: tümü isteğe bağlı; çıktı: servis firmasının adı da döner.
 */
const lifecycleInput = {
    installedOn: localDateSchema.nullable().default(null),
    warrantyEndsOn: localDateSchema.nullable().default(null),
    serviceCounterpartyId: uuidSchema.nullable().default(null),
};
const lifecycleOutput = {
    installedOn: localDateSchema.nullable(),
    warrantyEndsOn: localDateSchema.nullable(),
    serviceCounterpartyId: uuidSchema.nullable(),
    serviceCounterpartyName: z.string().nullable(),
};
const warrantyRule = (value, context) => {
    if (value.installedOn && value.warrantyEndsOn && value.warrantyEndsOn < value.installedOn)
        context.addIssue({
            code: "custom",
            path: ["warrantyEndsOn"],
            message: "Garanti bitişi kurulum tarihinden önce olamaz",
        });
};
// ── Tanklar ──────────────────────────────────────────────────────────────────
export const strappingSummarySchema = z.object({
    id: uuidSchema,
    versionNo: z.number().int(),
    validFrom: localDateSchema,
    rowCount: z.number().int(),
    maxHeightMm: z.string(),
    maxVolumeL: z.string(),
    note: z.string().nullable(),
    createdAt: timestampSchema,
});
export const tankSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    tankNo: z.number().int(),
    productId: uuidSchema,
    productName: z.string(),
    productColor: z.string().nullable(),
    nominalCapacityL: quantitySchema,
    safeFillPercent: z.string(),
    safeFillVolumeL: quantitySchema.describe("Nominal kapasite × güvenli doluluk üst sınırı"),
    minLevelL: quantitySchema,
    criticalLevelL: quantitySchema,
    gaugeProbeId: z.string().nullable(),
    serialNo: z.string().nullable(),
    ...lifecycleOutput,
    status: assetStatusSchema,
    activeStrappingTable: strappingSummarySchema.nullable(),
    version: z.number().int(),
});
const tankFields = z.object({
    tankNo: assetNo,
    productId: uuidSchema,
    nominalCapacityL: positiveQuantity,
    safeFillPercent: z
        .string()
        .regex(/^\d{1,3}(\.\d{1,2})?$/, "Yüzde en fazla 2 ondalıklı olmalıdır")
        .refine((value) => Number(value) > 0 && Number(value) <= 100, "Güvenli doluluk 0–100 arasında olmalıdır")
        .default("95"),
    minLevelL: nonNegativeQuantity.default("0"),
    criticalLevelL: nonNegativeQuantity.default("0"),
    gaugeProbeId: optionalText(60),
    serialNo: optionalText(80),
    ...lifecycleInput,
});
const tankRules = (value, context) => {
    if (value.minLevelL !== undefined &&
        value.criticalLevelL !== undefined &&
        Number(value.criticalLevelL) > Number(value.minLevelL))
        context.addIssue({
            code: "custom",
            path: ["criticalLevelL"],
            message: "Kritik seviye minimum seviyeden büyük olamaz",
        });
    if (value.minLevelL !== undefined &&
        value.nominalCapacityL !== undefined &&
        Number(value.minLevelL) >= Number(value.nominalCapacityL))
        context.addIssue({
            code: "custom",
            path: ["minLevelL"],
            message: "Minimum seviye nominal kapasiteden küçük olmalıdır",
        });
};
export const createTankSchema = tankFields.superRefine(tankRules).superRefine(warrantyRule);
export const updateTankSchema = patchOf(tankFields)
    .extend({ status: assetStatusSchema.optional(), version: z.number().int().positive() })
    .superRefine(tankRules)
    .superRefine(warrantyRule);
// ── Kalibrasyon tablosu ──────────────────────────────────────────────────────
export const strappingIssueSchema = z.object({
    rowNumber: z.number().int().nullable(),
    code: z.string(),
    severity: z.enum(["error", "warning"]),
    message: z.string(),
});
export const strappingPreviewRequestSchema = z.object({
    csv: z
        .string()
        .min(3, "Dosya boş")
        .max(1_000_000, "Dosya çok büyük (en fazla ~1 MB)")
        .describe("CSV içeriği: yükseklik (mm); hacim (L)"),
});
export const strappingPreviewSchema = z.object({
    valid: z.boolean(),
    rowCount: z.number().int(),
    maxHeightMm: z.string().nullable(),
    maxVolumeL: z.string().nullable(),
    issues: z.array(strappingIssueSchema).describe("Hatalı satır raporu; error varsa tablo kaydedilemez"),
    sample: z
        .array(z.object({ heightMm: z.string(), volumeL: z.string() }))
        .describe("İlk ve son satırlardan örnek"),
});
export const createStrappingTableSchema = strappingPreviewRequestSchema.extend({
    validFrom: localDateSchema,
    note: optionalText(300),
});
export const strappingTableListSchema = z.object({ items: z.array(strappingSummarySchema) });
export const tankVolumeQuerySchema = z.object({
    heightMm: z.string().regex(/^\d{1,5}(\.\d{1,2})?$/, "Yükseklik mm cinsinden, en fazla 2 ondalık"),
});
export const tankVolumeSchema = z.object({
    heightMm: z.string(),
    volumeL: quantitySchema,
    fillPercent: z.string(),
    strappingTableVersion: z.number().int(),
});
// ── Pompalar ve tabancalar ───────────────────────────────────────────────────
export const nozzleSchema = z.object({
    id: uuidSchema,
    dispenserId: uuidSchema,
    side: z.enum(["A", "B"]),
    nozzleNo: z.number().int(),
    productId: uuidSchema,
    productName: z.string(),
    productColor: z.string().nullable(),
    tankId: uuidSchema,
    tankNo: z.number().int(),
    currentTotalizer: quantitySchema.describe("Mevcut endeks (L)"),
    lastStampOn: localDateSchema.nullable(),
    nextStampDueOn: localDateSchema.nullable(),
    status: assetStatusSchema,
    version: z.number().int(),
});
export const dispenserSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    dispenserNo: z.number().int(),
    islandNo: z.number().int().nullable(),
    name: z.string().nullable(),
    brand: z.string().nullable(),
    model: z.string().nullable(),
    serialNo: z.string().nullable(),
    ...lifecycleOutput,
    status: assetStatusSchema,
    nozzles: z.array(nozzleSchema),
    version: z.number().int(),
});
const dispenserFields = z.object({
    dispenserNo: assetNo,
    islandNo: assetNo.nullable().default(null),
    name: optionalText(80),
    brand: optionalText(80),
    model: optionalText(80),
    serialNo: optionalText(80),
    ...lifecycleInput,
});
export const createDispenserSchema = dispenserFields.superRefine(warrantyRule);
export const updateDispenserSchema = patchOf(dispenserFields)
    .extend({
    status: assetStatusSchema.optional(),
    version: z.number().int().positive(),
})
    .superRefine(warrantyRule);
const nozzleFields = z.object({
    side: z.enum(["A", "B"]).default("A"),
    nozzleNo: assetNo,
    tankId: uuidSchema.describe("Tabancanın ürünü bağlı tankın ürünüdür"),
    currentTotalizer: nonNegativeQuantity.default("0"),
    lastStampOn: localDateSchema.nullable().default(null),
    nextStampDueOn: localDateSchema.nullable().default(null),
});
export const createNozzleSchema = nozzleFields;
export const updateNozzleSchema = patchOf(nozzleFields).extend({
    status: assetStatusSchema.optional(),
    version: z.number().int().positive(),
});
// ── Cihazlar ─────────────────────────────────────────────────────────────────
export const DEVICE_TYPES = [
    "TANK_GAUGE",
    "PUMP_CONTROLLER",
    "FISCAL_DEVICE",
    "POS_TERMINAL",
    "EV_CHARGER",
    "WASH_UNIT",
    "TTS_READER",
    "CAMERA",
    "OTHER",
];
export const deviceTypeSchema = z.enum(DEVICE_TYPES);
const hostSchema = z.string().trim().min(1).max(253);
const portSchema = z.number().int().min(1).max(65535);
const tcpConnection = z.object({ transport: z.literal("TCP"), host: hostSchema, port: portSchema });
const serialConnection = z.object({
    transport: z.literal("SERIAL"),
    port: z.string().trim().min(1).max(60).describe("COM3, /dev/ttyUSB0..."),
    baudRate: z.number().int().min(300).max(921600).default(9600),
});
const lineConnection = z.discriminatedUnion("transport", [tcpConnection, serialConnection]);
/**
 * Tür bazında bağlantı parametreleri. Parola/anahtar gibi gizli değerler burada TUTULMAZ; istasyon ajanının
 * yerel yapılandırmasında saklanır (Prompt 14). Bilinmeyen alanlar reddedilir (`strict`).
 */
export const DEVICE_CONNECTION_SCHEMAS = {
    TANK_GAUGE: lineConnection.and(z.object({ protocol: z.enum(["VEEDER_ROOT_TLS", "MODBUS", "OTHER"]).default("OTHER") })),
    PUMP_CONTROLLER: lineConnection.and(z.object({ protocol: z.enum(["IFSF", "GILBARCO_TWO_WIRE", "WAYNE_DART", "OTHER"]).default("OTHER") })),
    FISCAL_DEVICE: lineConnection.and(z.object({ fiscalId: z.string().trim().max(40).optional().describe("ÖKC sicil no") })),
    POS_TERMINAL: z
        .object({
        terminalId: z.string().trim().min(1).max(40),
        bank: z.string().trim().max(80).optional(),
        merchantId: z.string().trim().max(40).optional(),
    })
        .strict(),
    EV_CHARGER: z.object({ ocppIdentity: z.string().trim().min(3).max(48).optional() }).strict(),
    WASH_UNIT: z.union([lineConnection, z.object({ transport: z.literal("NONE") }).strict()]),
    TTS_READER: lineConnection,
    CAMERA: z.object({ streamUrl: z.url().max(500) }).strict(),
    OTHER: z.record(z.string(), z.union([z.string().max(500), z.number(), z.boolean()])),
};
export function parseDeviceConnection(deviceType, connection) {
    const result = DEVICE_CONNECTION_SCHEMAS[deviceType].safeParse(connection ?? {});
    return result.success
        ? { ok: true, value: result.data }
        : { ok: false, issues: result.error.issues };
}
export const deviceSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    deviceType: deviceTypeSchema,
    name: z.string(),
    brand: z.string().nullable(),
    model: z.string().nullable(),
    serialNo: z.string().nullable(),
    connection: z.record(z.string(), z.unknown()),
    ...lifecycleOutput,
    status: assetStatusSchema,
    version: z.number().int(),
});
const deviceFields = z.object({
    deviceType: deviceTypeSchema,
    name: z.string().trim().min(1, "Cihaz adı zorunludur").max(120),
    brand: optionalText(80),
    model: optionalText(80),
    serialNo: optionalText(80),
    connection: z.record(z.string(), z.unknown()).default({}),
    ...lifecycleInput,
});
export const createDeviceSchema = deviceFields.superRefine(warrantyRule);
export const updateDeviceSchema = patchOf(deviceFields.omit({ deviceType: true }))
    .extend({
    status: assetStatusSchema.optional(),
    version: z.number().int().positive(),
})
    .superRefine(warrantyRule);
// ── EV şarj üniteleri ve yıkama birimleri (tanım) ────────────────────────────
export const EV_CONNECTOR_TYPES = ["CCS2", "TYPE2", "CHADEMO"];
const powerSchema = z
    .string()
    .regex(/^\d{1,4}(\.\d{1,2})?$/, "Güç kW cinsinden, en fazla 2 ondalık")
    .refine((value) => Number(value) > 0, "Güç sıfırdan büyük olmalıdır");
export const evConnectorSchema = z.object({
    connectorNo: z.number().int().min(1).max(20),
    connectorType: z.enum(EV_CONNECTOR_TYPES),
    powerKw: powerSchema,
    status: assetStatusSchema.default("ACTIVE"),
});
export const evChargerSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    name: z.string(),
    ocppIdentity: z.string().nullable(),
    brand: z.string().nullable(),
    model: z.string().nullable(),
    maxPowerKw: z.string().nullable(),
    serialNo: z.string().nullable(),
    ...lifecycleOutput,
    status: assetStatusSchema,
    connectors: z.array(evConnectorSchema),
    version: z.number().int(),
});
const evChargerFields = z.object({
    name: z.string().trim().min(1, "Ünite adı zorunludur").max(120),
    ocppIdentity: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9._:-]{3,48}$/, "OCPP kimliği 3–48 karakter; harf, rakam ve . _ : - içerebilir")
        .nullable()
        .default(null),
    brand: optionalText(80),
    model: optionalText(80),
    maxPowerKw: powerSchema.nullable().default(null),
    serialNo: optionalText(80),
    ...lifecycleInput,
    connectors: z
        .array(evConnectorSchema)
        .max(20)
        .default([])
        .refine((items) => new Set(items.map((item) => item.connectorNo)).size === items.length, "Soket numaraları benzersiz olmalıdır"),
});
export const createEvChargerSchema = evChargerFields.superRefine(warrantyRule);
export const updateEvChargerSchema = patchOf(evChargerFields)
    .extend({
    status: assetStatusSchema.optional(),
    version: z.number().int().positive(),
})
    .superRefine(warrantyRule);
export const WASH_UNIT_TYPES = ["TUNNEL", "ROLLOVER", "SELF_SERVICE_BAY", "HAND_WASH"];
export const washUnitSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    unitType: z.enum(WASH_UNIT_TYPES),
    name: z.string(),
    serialNo: z.string().nullable(),
    ...lifecycleOutput,
    status: assetStatusSchema,
    version: z.number().int(),
});
const washUnitFields = z.object({
    unitType: z.enum(WASH_UNIT_TYPES),
    name: z.string().trim().min(1, "Birim adı zorunludur").max(120),
    serialNo: optionalText(80),
    ...lifecycleInput,
});
export const createWashUnitSchema = washUnitFields.superRefine(warrantyRule);
export const updateWashUnitSchema = patchOf(washUnitFields)
    .extend({
    status: assetStatusSchema.optional(),
    version: z.number().int().positive(),
})
    .superRefine(warrantyRule);
// ── İstasyon varlıkları (tek çağrıda yerleşim) ───────────────────────────────
export const stationAssetsSchema = z.object({
    stationId: uuidSchema,
    tanks: z.array(tankSchema),
    dispensers: z.array(dispenserSchema),
    devices: z.array(deviceSchema),
    evChargers: z.array(evChargerSchema),
    washUnits: z.array(washUnitSchema),
});
export const stationIdParamsSchema = z.object({ stationId: uuidSchema });
//# sourceMappingURL=assets.js.map